<template>
  <div class="upload-container">
    <div
      class="upload-drop-zone"
      :class="{
        'is-dragging': isDragging,
        'is-disabled': disabled,
      }"
      @click="openFile"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="handleDrop"
    >
      <template v-if="previewUrl">
        <div class="preview-wrapper mb-2">
          <img :src="previewUrl" class="preview-image" />
          <button class="btn-remove" @click.stop="removeFile">
            <i class="icon-fill_trash text-white"></i>
          </button>
        </div>

        <div class="text-muted font-size-12">
          {{ fileName }}
        </div>
      </template>

      <template v-else>
        <div class="upload-icon mb-3">
          <i :class="icon"></i>
        </div>

        <div class="text-muted mb-2 font-size-14">
          {{ title }}
        </div>

        <div class="text-muted mb-4">({{ subtitle }})</div>

        <McButton
          variant="outline-primary"
          :icon="buttonIcon"
          :text="buttonText"
          @click.stop="openFile"
        />
      </template>

      <!-- INPUT FILE -->
      <input
        ref="fileInput"
        type="file"
        class="d-none"
        :accept="accept"
        @change="handleFileSelect"
      />
    </div>
  </div>
</template>

<script>
import McButton from '@/components/McButton'

export default {
  name: 'McFileUploader',
  components: { McButton },

  props: {
    accept: {
      type: String,
      default: '.jpg,.png',
    },
    maxSize: {
      type: Number,
      default: 1 * 1024 * 1024, // 1MB
    },
    title: {
      type: String,
      default: 'Choose or drop file',
    },
    subtitle: {
      type: String,
      default: '.jpg / .png',
    },
    buttonText: {
      type: String,
      default: 'Choose File',
    },
    icon: {
      type: String,
      default: 'icon-outline_upload',
    },
    buttonIcon: {
      type: String,
      default: 'icon-fill_folder',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDragging: false,
      previewUrl: null,
      selectedFile: null,
    }
  },

  beforeUnmount() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }
  },
  computed: {
    fileName() {
      return this.selectedFile?.name || ''
    },
  },

  methods: {
    openFile() {
      if (this.disabled || this.previewUrl) return
      this.$refs.fileInput?.click()
    },

    onDragOver() {
      if (this.disabled || this.previewUrl) return
      this.isDragging = true
    },

    onDragLeave() {
      this.isDragging = false
    },

    handleDrop(e) {
      if (this.disabled || this.previewUrl) return
      this.isDragging = false

      const file = e.dataTransfer.files[0]
      if (file) this.validate(file)
    },

    handleFileSelect(e) {
      const file = e.target.files[0]
      if (file) this.validate(file)

      e.target.value = ''
    },

    validate(file) {
      if (file.size > this.maxSize) {
        this.$emit(
          'error',
          `${this.$t('rfidAndFacerecognition.upload.maxSizeLimit')} : ${
            this.maxSize / 1024 / 1024
          } MB`
        )
        return
      }

      const allowed = this.accept.split(',')
      const isValid = allowed.some((ext) => file.name.toLowerCase().endsWith(ext.trim()))

      if (!isValid) {
        this.$emit('error', this.$t('rfidAndFacerecognition.upload.invalidFormat'))
        return
      }

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }

      this.previewUrl = URL.createObjectURL(file)
      this.selectedFile = file

      this.$emit('selected', file)
    },
    removeFile() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }

      this.previewUrl = null
      this.selectedFile = null

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }

      this.$emit('selected', null)
    },

    clearPreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = null
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-container {
  width: 100%;
}

.upload-drop-zone {
  border: 2px dashed $teal-100;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background-color: $white;
  transition: all 0.25s ease;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: $primary;
    background-color: rgba($primary, 0.03);
  }

  &.is-dragging {
    border-color: $primary;
    background-color: rgba($primary, 0.08);
  }

  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .upload-icon {
    font-size: 28px;
    color: $primary;
  }

  .font-size-14 {
    font-size: 14px;
  }
}

.preview-wrapper {
  position: relative;
  display: inline-block;
}

.preview-image {
  max-width: 100%;
  max-height: 180px;
  border-radius: 8px;
  object-fit: cover;
}

.btn-remove {
  position: absolute;
  top: -6px;
  right: -6px;

  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  background-color: $danger;
  color: white;

  box-shadow: 0 2px 6px rgba(255, 77, 79, 0.4);

  &:hover {
    background-color: darken($danger, 8%);
  }
}
.btn-remove i {
  font-size: 12px;
}

.font-size-12 {
  font-size: 12px;
}
</style>
